import React, { useMemo } from 'react';
import { InfoIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  HStack,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Spinner,
  Switch,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { WATTWATCHERS_MANUFACTURER_ID } from 'clipsal-cortex-utils/src/constants/common-constants';

import { usePatchSiteMutation, useSite } from '../../../siteApi';
import { useSiteMeters } from '../../meterSetupApi';
import { selectSelectedMeterId, setSelectedMeterId } from '../circuitApplianceConfigSlice';

export const SelectMeter = ({ isDisabled }: { isDisabled: boolean }) => {
  const { siteMeters } = useSiteMeters();
  const { site } = useSite();
  const { clipsal_solar_id: siteId, block_third_party_updates: blockThirdPartyUpdates } = site;
  const [updateSite, { isLoading: isUpdatingSite }] = usePatchSiteMutation();
  const isMobileViewport = useBreakpointValue({ base: true, md: false }, { ssr: false });
  const { isOpen, onToggle, onClose } = useDisclosure();
  const selectedMeterId = useSelector(selectSelectedMeterId);
  const dispatch = useDispatch();

  const selectedManufacturerName = useMemo(() => {
    const selectedMeter = siteMeters.find((meter) => meter.row_id === selectedMeterId);
    return selectedMeter?.meta_data.manufacturer.manufacturer_name ?? '';
  }, [siteMeters, selectedMeterId]);

  const handleUpdateBlockThirdPartyUpdates = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      await updateSite({
        clipsal_solar_id: siteId,
        block_third_party_updates: event.target.checked,
      }).unwrap();
    } catch (error) {
      console.error('Failed to update block third party updates:', error);
    }
  };

  return (
    <HStack mb={4} spacing={4} flexDir={isMobileViewport ? 'column' : 'row'} w="100%" maxW={800}>
      <Box w="100%">
        <Text fontSize="md" fontWeight="bold">
          Select Meter
        </Text>
        <Select
          w="100%"
          maxW={250}
          data-testid="select-meter"
          placeholder=""
          disabled={isDisabled}
          value={selectedMeterId || ''}
          onChange={(e) => dispatch(setSelectedMeterId(Number(e.target.value)))}
        >
          {siteMeters.map((meter) => {
            const isWattwatcherMeter = meter.meta_data?.manufacturer_id === WATTWATCHERS_MANUFACTURER_ID;
            return (
              <option key={meter.row_id} value={meter.row_id ?? ''}>
                {isWattwatcherMeter ? meter.site_identifier : meter.serial_number}
              </option>
            );
          })}
        </Select>
      </Box>
      <Box w="100%">
        <Text fontSize="md" fontWeight="bold">
          Manufacturer
        </Text>
        <Input value={selectedManufacturerName} isReadOnly w="100%" maxW={250} variant={'filled'} />
      </Box>
      <Box w="100%">
        <Center justifyContent={'flex-start'}>
          <Text fontSize="md" fontWeight="bold" data-testid="block-third-party-updates-label">
            Lock Updates
          </Text>
          <Popover placement={isMobileViewport ? 'top' : 'bottom'} isOpen={isOpen} onClose={onClose}>
            <Button
              variant="unstyled"
              p={0}
              ml={2}
              minW={'fit-content'}
              h="fit-content"
              data-testid="block-third-party-updates-info-button"
              lineHeight={4}
              onClick={onToggle}
            >
              <PopoverTrigger>
                <InfoIcon h={4} w={4} color="blue.400" />
              </PopoverTrigger>
            </Button>
            <Portal>
              <PopoverContent borderRadius={8} shadow="md">
                <PopoverArrow />
                <PopoverBody data-testid="block-updates-info-popover">
                  Locking site updates will ensure no third party systems can update meter state of the site. Note: This
                  will also block updates to all meters of the site.
                </PopoverBody>
              </PopoverContent>
            </Portal>
          </Popover>
        </Center>
        <Center justifyContent={'flex-start'}>
          <Switch
            colorScheme="primaryBranding"
            data-testid="block-third-party-updates-switch"
            isChecked={blockThirdPartyUpdates}
            isDisabled={isUpdatingSite}
            onChange={handleUpdateBlockThirdPartyUpdates}
          />
          {isUpdatingSite && <Spinner ml={2} size={'sm'} color={'gray.400'} />}
        </Center>
      </Box>
    </HStack>
  );
};
